import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { auth } from "../supabase/client";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  CssBaseline,
  Divider,
  Fade,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Email as EmailIcon, Login as LoginIcon, PersonAdd as PersonAddIcon } from "@mui/icons-material";
import { ThemeProvider as CustomThemeProvider, useTheme } from "../contexts/ThemeContext";
import { useSystemButtonStyles } from "../hooks/useSystemButtonStyles";
import AuthPageToggleBar from "../components/AuthPageToggleBar";
import AuthPageBottomAnimation from "../components/AuthPageBottomAnimation";
import { requestPasswordResetEmail } from "../utils/api/api";
import {
  getPasswordResetRedirectUrl,
  mapPasswordResetRequestError,
} from "../utils/auth/passwordReset";
import {
  authAlertErrorSx,
  authAlertSuccessSx,
  authCardShellSx,
  authFieldSx,
  authLogoSx,
  authPageOuterSx,
  authPageContainerSx,
} from "../utils/authPageStyles";
import { APP_FONT_FAMILY } from "../constants/typography";
import { getMm } from "../theme/modernMaterialEnterprise";

const PasswordResetRequestContent = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { primaryButtonSx, outlineButtonSx } = useSystemButtonStyles();
  const fieldSx = useMemo(() => authFieldSx(isDarkMode), [isDarkMode]);
  const cardShellSx = useMemo(() => authCardShellSx(isDarkMode), [isDarkMode]);
  const logoSx = useMemo(() => authLogoSx(isDarkMode), [isDarkMode]);

  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handlePasswordResetRequest = async () => {
    if (!email.trim()) {
      setMessage(t("password_reset_enter_email"));
      return;
    }

    setIsLoading(true);
    setMessage("");
    setIsSuccess(false);

    const redirectTo = getPasswordResetRedirectUrl();
    const emailNorm = email.trim().toLowerCase();

    try {
      if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
        setMessage(t("password_reset_config_error"));
        return;
      }

      const apiBase = process.env.REACT_APP_CONNECTION_API;

      if (apiBase) {
        try {
          await requestPasswordResetEmail(emailNorm, redirectTo);
          setIsSuccess(true);
          setMessage(t("password_reset_success"));
          return;
        } catch (apiErr) {
          if (process.env.NODE_ENV === "development") {
            console.warn("Backend password reset failed, trying Supabase direct:", apiErr);
          }
          const status = apiErr.response?.status;
          if (status && status !== 504 && status < 500) {
            setMessage(apiErr.response?.data?.error || mapPasswordResetRequestError(apiErr, t));
            return;
          }
        }
      }

      const { error } = await auth.resetPasswordForEmail(emailNorm, { redirectTo });

      if (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Password reset error:", error, { redirectTo, status: error.status });
        }
        setMessage(mapPasswordResetRequestError(error, t));
      } else {
        setIsSuccess(true);
        setMessage(t("password_reset_success"));
      }
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("Password reset unexpected error:", err);
      }
      setMessage(mapPasswordResetRequestError(err, t));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <Box sx={{ ...authPageOuterSx(isDarkMode), position: "relative", overflow: "hidden" }}>
        <AuthPageBottomAnimation isDarkMode={isDarkMode} />
        <AuthPageToggleBar />
        <Container maxWidth="md" sx={authPageContainerSx}>
          <Fade in timeout={800}>
            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: "100%", sm: 440 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box sx={{ textAlign: "center", mb: 4, mt: { xs: 1, sm: 0 }, width: "100%" }}>
                <Box
                  component="img"
                  src={isDarkMode ? "NewEraLogo.png" : "NewEraBlackLogo.png"}
                  alt="NewEra Logo"
                  sx={logoSx}
                />
              </Box>

              <Card sx={{ ...cardShellSx, width: "100%" }}>
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                  <Box sx={{ textAlign: "center", mb: 3 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.875rem",
                        color: getMm(isDarkMode).onSurface,
                        mb: 1,
                        fontFamily: APP_FONT_FAMILY,
                      }}
                    >
                      {t("password_reset_title")}
                    </Typography>
                    <Typography
                      sx={{
                        color: getMm(isDarkMode).onSurfaceVariant,
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        fontFamily: APP_FONT_FAMILY,
                      }}
                    >
                      {t("password_reset_description")}
                    </Typography>
                  </Box>

                  {message && (
                    <Alert
                      severity={isSuccess ? "success" : "error"}
                      sx={{
                        mb: 3,
                        ...(isSuccess ? authAlertSuccessSx(isDarkMode) : authAlertErrorSx(isDarkMode)),
                      }}
                    >
                      {message}
                    </Alert>
                  )}

                  <TextField
                    label={t("password_reset_email_address")}
                    type="email"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !isLoading) handlePasswordResetRequest();
                    }}
                    fullWidth
                    disabled={isLoading}
                    autoComplete="email"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ ...fieldSx, mb: 2.5 }}
                  />

                  <Button
                    variant="contained"
                    onClick={handlePasswordResetRequest}
                    disabled={isLoading}
                    fullWidth
                    startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{ ...primaryButtonSx, py: 1.5, fontSize: "0.875rem", textTransform: "none" }}
                  >
                    {isLoading ? t("password_reset_sending") : t("password_reset_send_email")}
                  </Button>

                  <Divider sx={{ my: 2.5, borderColor: getMm(isDarkMode).outlineVariant }}>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        color: getMm(isDarkMode).onSurfaceVariant,
                        px: 2,
                        fontFamily: APP_FONT_FAMILY,
                      }}
                    >
                      {t("login_need_help")}
                    </Typography>
                  </Divider>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    <Button
                      component={Link}
                      to="/login"
                      variant="outlined"
                      fullWidth
                      startIcon={<LoginIcon />}
                      sx={{ ...outlineButtonSx, textTransform: "none", justifyContent: "flex-start" }}
                    >
                      {t("password_reset_sign_in")}
                    </Button>
                    <Button
                      component={Link}
                      to="/signup"
                      variant="outlined"
                      fullWidth
                      startIcon={<PersonAddIcon />}
                      sx={{ ...outlineButtonSx, textTransform: "none", justifyContent: "flex-start" }}
                    >
                      {t("password_reset_sign_up")}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Fade>
        </Container>
      </Box>
    </>
  );
};

const PasswordResetRequest = () => (
  <CustomThemeProvider>
    <PasswordResetRequestContent />
  </CustomThemeProvider>
);

export default PasswordResetRequest;
